import React, { useCallback, useEffect, useState } from 'react';
import LoginModal from './components/auth/LoginModal';
import RegisterModal from './components/auth/RegisterModal';
import ChangePasswordModal from './components/auth/ChangePasswordModal';
import ForgotPasswordModal from './components/auth/ForgotPasswordModal';
import Header from './components/layout/Header.jsx';
import Footer from './components/layout/Footer.jsx';
import { toaster } from './components/ui/toaster';
import EventList from './components/events/EventList';
import SupportPage from './components/pages/SupportPage';
import ClockPage from './components/pages/ClockPage';
import PrivacyPage from './components/pages/PrivacyPage';
import AevdirPage from './components/pages/AevdirPage';
import ProfilePage from './components/pages/ProfilePage';
import { AuthProvider, useAuth } from './context/AuthContext';
import { Provider } from './components/ui/provider';
import {
  detectLanguageByGeo,
  getStoredLanguagePreference,
  setStoredLanguagePreference,
} from './utils/languagePreference';
import './App.css';

const getCurrentPath = () => {
  if (typeof window === 'undefined') return '/';
  return window.location.pathname || '/';
};

function AppContent() {
  const { user, login, register, logout, changePassword, markEmailVerified, refreshProfile } = useAuth();
  const [mode, setMode] = useState('none');
  const [lang, setLang] = useState(() => getStoredLanguagePreference() || 'en');
  const [path, setPath] = useState(() => getCurrentPath());

  const isEn = lang === 'en';

  const showToast = useCallback((text) => {
    toaster.create({
      description: text,
      type: 'info',
      closable: true,
    });
  }, []);

  useEffect(() => {
    const handlePop = () => setPath(getCurrentPath());
    window.addEventListener('popstate', handlePop);
    return () => window.removeEventListener('popstate', handlePop);
  }, []);

  const navigate = useCallback((to) => {
    if (typeof window === 'undefined') return;
    if (getCurrentPath() !== to) {
      window.history.pushState({}, '', to);
    }
    setPath(to);
  }, []);

  useEffect(() => {
    const storedLang = getStoredLanguagePreference();
    if (storedLang) return;

    let cancelled = false;

    const initLanguage = async () => {
      const detectedLang = await detectLanguageByGeo();
      if (cancelled) return;
      setLang(detectedLang);
      setStoredLanguagePreference(detectedLang);
    };

    initLanguage();

    return () => {
      cancelled = true;
    };
  }, []);

  const doRegister = async (payload) => {
    const auth = await register(payload);
    showToast(isEn ? `Welcome, ${auth.userName}` : `欢迎，${auth.userName}`);
    return auth;
  };

  const doLogin = async (payload) => {
    const auth = await login(payload);
    setMode('none');
    showToast(isEn ? `Welcome back, ${auth.userName}` : `欢迎回来，${auth.userName}`);
  };

  const doLogout = async () => {
    try {
      await logout();
    } finally {
      showToast(isEn ? 'Signed out' : '已退出登录');
    }
  };

  const doChangePassword = async (payload) => {
    await changePassword(payload);
    setMode('none');
    showToast(isEn ? 'Password updated successfully' : '密码修改成功');
  };

  const handleToggleLang = () => {
    setLang((prev) => {
      const nextLang = prev === 'en' ? 'zh' : 'en';
      setStoredLanguagePreference(nextLang);
      return nextLang;
    });
  };

  const isSupportPage = path === '/page/support';
  const isClockPage = path === '/page/clock';
  const isPrivacyPage = path === '/page/privacy';
  const isProfilePage = path === '/page/profile';
  const isAevdirPage = path === '/' || path === '/page/aevdir';
  const isHappyDogPage = path === '/page/happy-dog';

  let pageContent;
  if (isAevdirPage) {
    pageContent = <AevdirPage lang={lang} />;
  } else if (isSupportPage) {
    pageContent = <SupportPage lang={lang} />;
  } else if (isClockPage) {
    pageContent = (
      <ClockPage lang={lang} onNavigateSupport={() => navigate('/page/support')} />
    );
  } else if (isPrivacyPage) {
    pageContent = <PrivacyPage lang={lang} />;
  } else if (isProfilePage) {
    pageContent = (
      <ProfilePage
        lang={lang}
        onLogin={() => setMode('login')}
        onChangePassword={() => setMode('password')}
        onNotify={showToast}
        onSaved={(profile) => {
          showToast(isEn ? `Profile saved, ${profile.name}` : `个人信息已保存，${profile.name}`);
        }}
      />
    );
  } else if (isHappyDogPage) {
    pageContent = <EventList lang={lang} />;
  } else {
    pageContent = <AevdirPage lang={lang} />;
  }

  return (
    <div className="App">
      <Header
        user={user}
        lang={lang}
        onLogin={() => setMode('login')}
        onRegister={() => setMode('register')}
        onLogout={doLogout}
        onToggleLang={handleToggleLang}
        onNavigateSupport={() => navigate('/page/support')}
        onNavigateHome={() => navigate('/')}
        onNavigatePrivacy={() => navigate('/page/privacy')}
        onNavigateProfile={() => navigate('/page/profile')}
        onNavigateClock={() => navigate('/page/clock')}
        onNavigateAevdir={() => navigate('/page/aevdir')}
        onNavigateHappyDog={() => navigate('/page/happy-dog')}
        currentPath={path}
      />
      <main className="content">
        {pageContent}
        <LoginModal
          open={mode === 'login'}
          onClose={() => setMode('none')}
          onSubmit={doLogin}
          onSwitch={() => setMode('register')}
          onForgotPassword={() => setMode('forgot-password')}
          lang={lang}
        />
        <RegisterModal
          open={mode === 'register'}
          onClose={() => setMode('none')}
          onSubmit={doRegister}
          onSwitch={() => setMode('login')}
          onVerified={async (email) => {
            markEmailVerified(email);
            try {
              await refreshProfile();
            } catch (_) {
              // optimistic update already applied
            }
            setMode('none');
            showToast(isEn ? 'Email verified successfully' : '邮箱验证成功');
          }}
          lang={lang}
        />
        <ForgotPasswordModal
          open={mode === 'forgot-password'}
          onClose={() => setMode('none')}
          onSuccess={async () => {
            await logout();
            setMode('login');
            showToast(isEn ? 'Password reset. Please sign in again.' : '密码已重置，请重新登录。');
          }}
          lang={lang}
        />
        <ChangePasswordModal
          open={mode === 'password'}
          onClose={() => setMode('none')}
          onSubmit={doChangePassword}
          lang={lang}
        />
      </main>
      <Footer lang={lang} />
    </div>
  );
}

function App() {
  return (
    <Provider>
      <AuthProvider>
        <AppContent />
      </AuthProvider>
    </Provider>
  );
}

export default App;
